import {
	faCheckCircle,
	faLock,
	faMapLocation,
	faPlus,
	faSquare,
	faSquareCheck,
	faStar,
	faWarning,
	faXmark,
} from "@fortawesome/pro-regular-svg-icons";

import { differenceInDays, differenceInMonths, differenceInYears, format } from "date-fns";

import { CircularProgress, Link } from "@mui/material";

import {
	ChangeEvent,
	Dispatch,
	FunctionComponent,
	MouseEvent as ReactMouseEvent,
	SetStateAction,
	useEffect,
	useState,
	useRef,
	useMemo,
} from "react";

import ReactDOM from "react-dom";

import { toLeadScoreText } from "@/components/LeadScoringPopover/scoreTier";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { InfoCardPopover, InfoCardPopoverRow } from "@/components/InfoCardPopover";

import { EmailIcon } from "@/components/EmailIcon/EmailIcon";

import { LinkedinIcon } from "@/components/LinkedinIcon/LinkedinIcon";

import { ExternalLink } from "@/components/ExternalLink/ExternalLink";

import { PhoneIcon } from "@/components/PhoneIcon/PhoneIcon";

import { Icon } from "@/components";
import { TableColumn } from "@/components/Table/Table";

import { SortParams } from "@/types/types";

import { resolveLeadLocation } from "@/pages/Private/pages/Contacts/utils/resolveLeadLocation";
import { classNames } from "@/utils/classNames";
import { ensureHttpProtocol } from "@/utils/url";
import { Checkbox } from "@/components/Checkbox/Checkbox";

import { InputField } from "@/components/InputField/InputField";

import {
	useUpdateLeadScoringResultMutation,
	useUpdateMapBusinessMutation,
	useUpdateOrganizationMutation,
	useUpdateProspectMutation,
	useUpdateSignalPriorityMutation,
} from "@/pages/Private/redux/list/list.api";
import { updateProspectGenderInInbox } from "@/pages/Private/redux/list/list.slice";
import { useAppDispatch } from "@/redux/hooks";

import { WebsiteIcon } from "@/components/WebsiteIcon/WebsiteIcon";

import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { Dropdown } from "@/components/Dropdown/Dropdown";

import { TextArea } from "@/components/TextArea/TextArea";

import { Button } from "@/components/Button/Button";

import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { getGenderConfidenceState } from "@/utils/genderConfidence";

import { useAnchoredTooltip } from "@/utils/useAnchoredTooltip";

import { useCloseOnOutsideClick } from "@/utils/useCloseOnOutsideClick";

import { HideOnErrorImg } from "@/components/HideOnErrorImg/HideOnErrorImg";

import { renderCurrentScoreResult } from "./renderCurrentScoreResult";
import { OfferEmailStatusBadge } from "./OfferEmailStatusBadge";

import { getOrderStatus, formatEmployees } from "./column-utils";
import {
	renderFoundedYearColumn,
	renderIndustryColumn,
	renderNormalizedCompanyNameColumn,
} from "./columns/company-facts-columns";
import { ListPerson } from "../schema/list";
import {
	CompanyCategoryResults,
	PersonaCategoryResults,
	ProspectEmployments,
} from "../../Apollo/schema/get-people";

import { ModalChangeCompanyCategory } from "../components/Scoring/ModalChangeCompanyCategory";
import { ModalChangePersonaCategory } from "../components/Scoring/ModalChangePersonaCategory";
import { ModalChangeSeniority } from "../components/GeneralTableModals/ModalChangeSeniority";
import { CrmIcon } from "../components/CrmIcon";
import { DeepResearchStatusCell } from "../components/CustomColumns/DeepResearchStatusCell";

import { EditableNameColumn } from "./columns/EditableNameColumn";

// TrackerPerson components (TrackerPersonAvatar, TrackerPersonRow, TrackerPersonSelectionModal, TrackerPersonsCell)
// are now imported from ./columns/TrackerPersonComponents.tsx
import { TrackerPersonsCell } from "./columns/TrackerPersonComponents";
import { TruncatedText, truncateString } from "./columns/TruncatedText";
import { TrackerSignalCell } from "./TrackerSignalCell";
import { LeadColorDots } from "../components/CampaignOverview/components/Lead/components/LeadColorDots";

export { renderAssignToColumn } from "./columns/AssignToCampaignColumn";

// Re-export extracted column modules
export {
	renderGoogleMapsPinterestColumn,
	renderGoogleMapsGithubColumn,
	renderGoogleMapsYoutubeColumn,
	renderGoogleMapsTwitterColumn,
	renderGoogleMapsSnapchatColumn,
	renderGoogleMapsInstagramColumn,
	renderGoogleMapsTiktokColumn,
	renderGoogleMapsYelpColumn,
	renderGoogleMapsFacebookColumn,
} from "./columns/socialMediaColumns";

export {
	renderSalutationDeColumn,
	renderSalutationEnColumn,
	renderJobSalutationDeColumn,
	renderJobSalutationEnColumn,
	renderMapSalutationDeColumn,
	renderMapSalutationEnColumn,
} from "./columns/salutationColumns";

export {
	TrackerPersonAvatar,
	TrackerPersonRow,
	TrackerPersonSelectionModal,
	TrackerPersonsCell,
} from "./columns/TrackerPersonComponents";
export { TruncatedText, truncateString };
export { DEFAULT_COLUMN_OPTIONS_SHOW, DEFAULT_COLUMN_OPTIONS_HIDE } from "./columns/columnFactory";

export const renderLanguageColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.language";

	return {
		title: ts("language"),
		width: "fit-content",
		orderTitle: "prospect.language",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "language",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => <div>{row.prospect?.language}</div>,
	};
};

export const renderPersonCountryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.country";

	return {
		title: ts("country"),
		width: "fit-content",
		orderTitle: "prospect.country",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "personLocationCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={(event: ReactMouseEvent) => {
						event.preventDefault();
						event.stopPropagation();
						if (row.prospect?.country) {
							handleCopyClick(row.prospect?.country);
						}
					}}
				>
					{row.prospect?.country}
				</button>
			</div>
		),
	};
};

export const renderPersonStateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.state";

	return {
		title: ts("state"),
		width: "fit-content",
		orderTitle: "prospect.state",
		uid: "personLocationState",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (row.prospect?.state) {
							handleCopyClick(row.prospect?.state);
						}
					}}
				>
					{row.prospect?.state}
				</button>
			</div>
		),
	};
};

export const renderPersonCityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.city";

	return {
		title: ts("city"),
		width: "fit-content",
		orderTitle: "prospect.city",
		uid: "personLocationCity",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						if (row.prospect?.city) {
							handleCopyClick(row.prospect?.city);
						}
					}}
				>
					{row.prospect?.city}
				</button>
			</div>
		),
	};
};

export const renderReportetEmployeesColumn = (
	_ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.employeesFrom";

	return {
		title: "Reported employees",
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "reportedEmployees",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer mr-2"
					onClick={(e) => {
						e.stopPropagation();
						e.preventDefault();

						handleCopyClick(
							`${row.prospect?.organization?.employeesFrom?.toString()}${
								row.prospect?.organization?.employeesTo
									? ` - ${row.prospect?.organization?.employeesTo}`
									: ""
							}`
						);
					}}
				>
					{row.prospect?.organization?.employeesFrom}
					{row.prospect?.organization?.employeesTo &&
						row.prospect?.organization?.employeesTo !== row.prospect?.organization?.employeesFrom &&
						` - ${row.prospect?.organization?.employeesTo}`}
				</button>
			</div>
		),
	};
};

export const renderEstimatedNumEmployeesColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.estimatedNumEmployees";

	return {
		title: ts("companyEmployees"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "companyEmployees",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<Link
					href={
						row.prospect?.organization?.linkedinUrl
							? `${row.prospect?.organization?.linkedinUrl}/people`
							: undefined
					}
					target="_blank"
					className="cursor-pointer mr-2"
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					{row.prospect?.organization?.estimatedNumEmployees}{" "}
				</Link>
			</div>
		),
	};
};

export const renderFollowers = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.followers";

	return {
		title: ts("followers"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "followers",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button className="mr-2">{row.prospect?.followers}</button>
			</div>
		),
	};
};

export const renderAboutColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.about";

	return {
		title: ts("about"),
		width: "fit-content",
		orderTitle: orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "about",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				<Tooltip
					content={
						<div className="flex flex-col min-w-[300px] text-sm">
							<div>
								<span
									className="leading-6 truncate ..."
									style={{
										textWrap: "wrap",
									}}
								>
									{row.prospect?.about}
								</span>
							</div>
						</div>
					}
				>
					<button
						className="cursor-pointer mr-1  max-w-[200px] text-ellipsis overflow-hidden"
						onClick={() => row.prospect?.about && handleCopyClick(row.prospect?.about)}
					>
						{row.prospect?.about}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderHeadlineColumn = (
	_ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "prospect.headline";

	return {
		title: "Headline",
		width: "fit-content",
		orderTitle: orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "headline",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				<Tooltip
					content={
						<div className="flex flex-col min-w-[300px] text-sm">
							<div>
								<span
									className="leading-6 truncate ..."
									style={{
										textWrap: "wrap",
									}}
								>
									{row.prospect?.headline}
								</span>
							</div>
						</div>
					}
				>
					<button
						className="cursor-pointer mr-1  max-w-[200px] text-ellipsis overflow-hidden"
						onClick={() => row.prospect?.headline && handleCopyClick(row.prospect?.headline)}
					>
						{row.prospect?.headline}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderJobCityColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "offer.jobCity";

	return {
		title: ts("jobCity"),
		orderTitle: orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "jobCity",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => <div className="flex flex-row">{row.offer?.jobCity}</div>,
	};
};

export const renderCompanyPeopleCheckColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("companyPeopleCheck"),
		uid: "companyPeopleCheck",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) =>
			row.companyPeopleCheck ? (
				<Tooltip
					content={
						<div className="w-full flex flex-col">
							<div>
								{row.companyPeopleCheck?.assignedAllTime} people have been assigned all-time
							</div>
							<div>
								{row.companyPeopleCheck?.assignedOnThisList} people have been assigned of this list
							</div>
							<div>
								{row.companyPeopleCheck?.notAssignedOnThisList} people are still on this list
							</div>
						</div>
					}
				>
					<div className="flex flex-row">
						{row.companyPeopleCheck?.assignedAllTime} | {row.companyPeopleCheck.assignedOnThisList}{" "}
						| {row.companyPeopleCheck.notAssignedOnThisList}
					</div>
				</Tooltip>
			) : (
				<></>
			),
	};
};

export const renderJobGenderColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "offer.gender";

	return {
		title: ts("gender"),
		orderTitle: orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "gender",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				<button
					className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();

						handleCopyClick(row.offer?.gender ?? "");
					}}
				>
					{row.offer?.gender}
				</button>
			</div>
		),
	};
};

export const renderJobCountryColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "offer.jobCountry";

	return {
		title: ts("jobCountry"),
		orderTitle: orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "jobCountry",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => <div className="flex flex-row">{row.offer?.jobCountry}</div>,
	};
};

export const renderConnectionsColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "user_prospect.userId";

	return {
		title: ts("connections"),
		orderTitle: orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "connections",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				{row.connections?.map((user: string, index: number) => (
					<div key={`connections-${index * 2}`} className="flex text-nowrap">
						<div className="flex flex-row w-fit items-center">
							<div className="flex text-xs mr-2 text-white min-w-[26px] w-[26px] max-w-[26px] h-[26px] items-center justify-center rounded-full bg-brand-light p-1">
								{user.split(" ")[0][0]}
								{user.split(" ")[1][0]}
							</div>
						</div>
					</div>
				))}
			</div>
		),
	};
};

export const renderRoleChangeColumn = (): TableColumn => {
	return {
		title: "Role Change",
		uid: "roleChange",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			const timeSinceCurrentJobStarted = row.prospect?.startedCurrentPosition
				? new Date(row.prospect?.startedCurrentPosition)
				: null;

			if (!timeSinceCurrentJobStarted) {
				return <div className="flex flex-row">N/A</div>;
			}

			const daysSinceCurrentJobStarted = differenceInDays(new Date(), timeSinceCurrentJobStarted);

			const monthsSinceCurrentJobStarted = differenceInMonths(
				new Date(),
				timeSinceCurrentJobStarted
			);

			const yearsSinceCurrentJobStarted = differenceInYears(new Date(), timeSinceCurrentJobStarted);

			let textIs = "No information found";

			if (row.prospect?.jobChangeOrPromotion) {
				textIs = `${row.prospect?.jobChangeOrPromotion} `;
				if (yearsSinceCurrentJobStarted > 0) {
					textIs += `${yearsSinceCurrentJobStarted}+ years ago`;
				} else if (monthsSinceCurrentJobStarted > 0) {
					textIs += `${monthsSinceCurrentJobStarted}+ months ago`;
				} else if (daysSinceCurrentJobStarted > 0) {
					textIs += `${daysSinceCurrentJobStarted}+ days ago`;
				}
			}

			return (
				<div className="flex flex-row">
					<Tooltip
						content={
							<div className="flex flex-col gap-4">
								{row.prospect?.jobChangeOrPromotion} in{" "}
								{format(timeSinceCurrentJobStarted, "MMMM yyyy")}
							</div>
						}
					>
						<div className="flex text-nowrap">
							<div className="flex flex-row w-fit items-center">{textIs}</div>
						</div>
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderPreviousEmployersColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("previousEmployers"),
		uid: "previousEmployers",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			const previousEmployers = row.prospect?.prospectEmployments
				?.filter((employer: ProspectEmployments) => {
					return (
						row.prospect?.organization?.name &&
						!employer.organizationName.includes(row.prospect?.organization?.name) &&
						!row.prospect?.organization?.name.includes(employer.organizationName)
					);
				})
				.sort((a, b) => {
					return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
				});

			const previousEmployer =
				previousEmployers && previousEmployers?.length > 0 ? previousEmployers[0] : null;

			return (
				<div className="flex flex-row">
					<Tooltip
						content={
							<div className="flex flex-col gap-4">
								{previousEmployers?.map((employer: ProspectEmployments) => (
									<div
										key={`employer-${employer.id}-${employer.title}-${employer.organizationName}`}
										className="flex"
									>
										{employer.title} @ {employer.organizationName}
									</div>
								))}
							</div>
						}
					>
						<div className="flex text-nowrap">
							<div className="flex flex-row w-fit items-center">
								{previousEmployer?.title
									? `${previousEmployer?.title} @ ${previousEmployer?.organizationName}`
									: ""}
							</div>
						</div>
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderPreviousEmployerColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("previousEmployer"),
		uid: "previousEmployer",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			const previousEmployers = row.prospect?.prospectEmployments
				?.filter((employer: ProspectEmployments) => {
					return (
						row.prospect?.organization?.name &&
						!employer.organizationName.includes(row.prospect?.organization?.name) &&
						!row.prospect?.organization?.name.includes(employer.organizationName)
					);
				})
				.sort((a, b) => {
					return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
				});

			const previousEmployer =
				previousEmployers && previousEmployers?.length > 0 ? previousEmployers[0] : null;

			return (
				<div className="flex flex-row">
					<Tooltip
						content={
							<div className="flex flex-col gap-4">
								{previousEmployers?.map((employer: ProspectEmployments) => (
									<div
										key={`employer-${employer.id}-${employer.title}-${employer.organizationName}`}
										className="flex"
									>
										{employer.organizationName}
									</div>
								))}
							</div>
						}
					>
						<div className="flex text-nowrap">
							<div className="flex flex-row w-fit items-center">
								{previousEmployer?.organizationName}
							</div>
						</div>
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderPreviousJobTitleColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("previousJobTitle"),
		uid: "previousJobTitle",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			const previousJobTitles = row.prospect?.prospectEmployments
				?.filter((employer: ProspectEmployments) => {
					return (
						row.prospect?.title &&
						!employer.title.includes(row.prospect?.title) &&
						!row.prospect?.title.includes(employer.title)
					);
				})
				.sort((a, b) => {
					return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
				});

			const previousJobTitle =
				previousJobTitles && previousJobTitles?.length > 0 ? previousJobTitles[0] : null;

			return (
				<div className="flex flex-row">
					<Tooltip
						content={
							<div className="flex flex-col gap-4">
								{previousJobTitles?.map((jobTitle: ProspectEmployments) => (
									<div
										key={`jobTitle-${jobTitle.id}-${jobTitle.title}-${jobTitle.organizationName}`}
										className="flex"
									>
										{jobTitle.title}
									</div>
								))}
							</div>
						}
					>
						<div className="flex text-nowrap">
							<div className="flex flex-row w-fit items-center">{previousJobTitle?.title}</div>
						</div>
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderCompanyColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: ({ id, name }: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "organization.name";

	return {
		title: ts("company"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		minWidth: "270px",
		calcWidth: 270,
		uid: "company",
		hideable: true,
		locked: "leftFifth",
		moveable: false,
		show: true,
		render: (row: ListPerson) => (
			<EditableCompanyColumn
				{...{
					row,
					handleCopyClick,
					orderTitle,
					setShowEdit,
					showEdit,
					sortingInbox,
				}}
			/>
		),
	};
};

export const renderJobsCompanyColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: ({ id, name }: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined,
	nameColumnShown: boolean
): TableColumn => {
	const orderTitle = "offer.companyName";

	return {
		title: ts("company"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		minWidth: "220px",
		uid: "company",
		hideable: true,
		locked: nameColumnShown ? "leftFifth" : "leftFourth",
		moveable: false,
		show: true,
		render: (row: ListPerson) => (
			<EditableJobsCompanyColumn
				{...{
					row,
					handleCopyClick,
					orderTitle,
					setShowEdit,
					showEdit,
					sortingInbox,
				}}
			/>
		),
	};
};

/// fundingMa

export const renderFundingMaAmountColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaAmount"),
		width: "fit-content",
		uid: "fundingMaAmount",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex">
				{row.fundingMaCompanyPerson?.fundingMaCompany?.amount}{" "}
				{row.fundingMaCompanyPerson?.fundingMaCompany?.currency}
			</div>
		),
	};
};

export const renderFundingMaRoundColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaRound"),
		width: "fit-content",
		uid: "fundingMaRound",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex">{row.fundingMaCompanyPerson?.fundingMaCompany?.round}</div>
		),
	};
};

export const renderFundingMaInvestorsColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaInvestors"),
		width: "fit-content",
		uid: "fundingMaInvestors",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex">
				<Tooltip
					content={
						<div>
							{row.fundingMaCompanyPerson?.fundingMaCompany?.investors?.map(
								(investor: { name: string; linkedin_url: string }) => (
									<div key={investor.name} className="flex flex-row gap-1">
										{investor.name}
										{investor.linkedin_url && (
											<Link href={ensureHttpProtocol(investor.linkedin_url)} target="_blank">
												<LinkedinIcon status="checked" />
											</Link>
										)}
									</div>
								)
							)}
						</div>
					}
				>
					<div className="flex flex-row gap-1">
						{row.fundingMaCompanyPerson?.fundingMaCompany?.investors?.[0]?.name || ""}
						{row.fundingMaCompanyPerson?.fundingMaCompany?.investors?.[0]?.linkedin_url && (
							<Link
								href={ensureHttpProtocol(
									row.fundingMaCompanyPerson?.fundingMaCompany?.investors?.[0]?.linkedin_url
								)}
								target="_blank"
							>
								<LinkedinIcon status="checked" />
							</Link>
						)}
						{row.fundingMaCompanyPerson?.fundingMaCompany?.investors?.length &&
						row.fundingMaCompanyPerson?.fundingMaCompany?.investors?.length > 1
							? "..."
							: ""}
					</div>
				</Tooltip>
			</div>
		),
	};
};

export const renderFundingMaPercentageColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaPercentage"),
		width: "fit-content",
		uid: "fundingMaPercentage",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex">{row.fundingMaCompanyPerson?.fundingMaCompany?.percentage}</div>
		),
	};
};

export const renderFundingMaPostUrlColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaPostUrl"),
		width: "fit-content",
		uid: "fundingMaPostUrl",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex">
				{row.fundingMaCompanyPerson?.fundingMaCompany?.postUrl && (
					<Link
						href={ensureHttpProtocol(row.fundingMaCompanyPerson?.fundingMaCompany?.postUrl)}
						target="_blank"
					>
						<ExternalLink />
					</Link>
				)}
			</div>
		),
	};
};

export const renderFundingMaAnnouncedColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaAnnounced"),
		width: "fit-content",
		uid: "fundingMaAnnounced",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex">
				{row.fundingMaCompanyPerson?.fundingMaCompany?.date
					? format(
							new Date(row.fundingMaCompanyPerson?.fundingMaCompany?.date as string),
							"dd.MM.yyyy"
						)
					: ""}
			</div>
		),
	};
};

export const renderFundingMaAcquiredByNameColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("fundingMaAcquiredByName"),
		width: "fit-content",
		uid: "fundingMaAcquiredByName",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row gap-1">
				{row.fundingMaCompanyPerson?.fundingMaCompany?.acquiredByName}
				{row.fundingMaCompanyPerson?.fundingMaCompany?.acquiredByLinkedinUrl && (
					<Link
						href={ensureHttpProtocol(
							row.fundingMaCompanyPerson?.fundingMaCompany?.acquiredByLinkedinUrl
						)}
						target="_blank"
					>
						<LinkedinIcon />
					</Link>
				)}
			</div>
		),
	};
};

export const renderFundingMaCompanyInterestedInColumn = (
	ts: (key: string) => string
): TableColumn => {
	return {
		title: ts("fundingMaCompanyInterestedIn"),
		width: "fit-content",
		uid: "fundingMaCompanyInterestedIn",
		hideable: true,
		moveable: true,
		show: true,
		minWidth: "220px",
		render: (row: ListPerson) => (
			<div className="flex w-[200px] text-ellipsis overflow-hidden">
				<Tooltip
					content={row.fundingMaCompanyPerson?.fundingMaCompany?.companyInterestedIn?.join("\n\n ")}
				>
					<div className="block w-[200px] text-ellipsis overflow-hidden">
						{row.fundingMaCompanyPerson?.fundingMaCompany?.companyInterestedIn?.join(", ")}
					</div>
				</Tooltip>
			</div>
		),
	};
};

export const renderMapCompanyColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	lockedClassName?: string
): TableColumn => {
	const orderTitle = "map_business.name";

	return {
		title: ts("company"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		minWidth: "220px",
		uid: "companyName",
		hideable: true,
		locked: lockedClassName || "leftFourth",
		moveable: false,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				<div className="ml-2 w-full flex flex-row justify-between">
					<Tooltip content={row.mapBusiness?.companyName}>
						<button
							className="cursor-pointer mr-1  max-w-[100px] text-ellipsis overflow-hidden"
							onClick={() =>
								row.mapBusiness?.companyName && handleCopyClick(row.mapBusiness?.companyName)
							}
						>
							{row.mapBusiness?.companyName}
						</button>
					</Tooltip>
					<div className="flex items-center">
						{row.mapBusiness?.companyEmail ? (
							<Tooltip content={row.mapBusiness?.companyEmail}>
								<button
									className="cursor-pointer"
									type="button"
									onClick={() =>
										row.mapBusiness?.companyEmail && handleCopyClick(row.mapBusiness?.companyEmail)
									}
								>
									<EmailIcon status="checked" />
								</button>
							</Tooltip>
						) : (
							<EmailIcon status="failed" />
						)}
						{row.mapBusiness?.phoneNumber ? (
							<Tooltip content={row.mapBusiness?.phoneNumber}>
								<button
									aria-label="copy phoneNumber"
									className="mr-1 cursor-pointer"
									type="button"
									onClick={(e: ReactMouseEvent) => {
										e.preventDefault();
										e.stopPropagation();
										handleCopyClick(row.mapBusiness?.phoneNumber || "");
									}}
								>
									<PhoneIcon status="checked" />
								</button>
							</Tooltip>
						) : (
							<PhoneIcon status="failed" />
						)}
						{row.mapBusiness?.companyLinkedinUrl ? (
							<Tooltip content={row.mapBusiness?.companyLinkedinUrl}>
								<button
									aria-label="copy phoneNumber"
									className="mr-1 cursor-pointer"
									type="button"
									onClick={(e: ReactMouseEvent) => {
										e.preventDefault();
										e.stopPropagation();
										handleCopyClick(row.mapBusiness?.companyLinkedinUrl || "");
									}}
								>
									<LinkedinIcon status="checked" />
								</button>
							</Tooltip>
						) : (
							<LinkedinIcon status="failed" />
						)}
					</div>
				</div>
			</div>
		),
	};
};

export const renderGoogleMapsColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_business.placeLink";

	return {
		title: ts("googleMaps"),
		width: "fit-content",
		orderTitle,
		uid: "placeLink",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				{row.mapBusiness?.placeLink && (
					<Link href={ensureHttpProtocol(row.mapBusiness?.placeLink)} target="_blank">
						<Icon className="h-[16px] flex mr-2" icon={faMapLocation} />
					</Link>
				)}
			</div>
		),
	};
};

export const renderGoogleMapsReviewAmountColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_business.reviewCount";

	return {
		title: ts("reviewCount"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "reviewCount",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => <div>{row.mapBusiness?.reviewCount}</div>,
	};
};

export const renderGoogleMapsAverageRatingColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_business.rating";

	return {
		title: ts("rating"),
		width: "fit-content",
		orderTitle: "rating",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "rating",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				<button
					onClick={() =>
						row.mapBusiness?.rating && handleCopyClick(row.mapBusiness?.rating.toString())
					}
				>
					{row.mapBusiness?.rating}
				</button>
			</div>
		),
	};
};

export const renderGoogleMapsTypeColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderStatus = getOrderStatus(sortingInbox, "map_business.type");

	return {
		title: ts("type"),
		width: "fit-content",
		orderTitle: "map_business.type",
		orderStatus,
		uid: "type",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => <div>{row.mapBusiness?.type}</div>,
	};
};

// Social media columns (Pinterest, Github, Youtube, Twitter, Snapchat, Instagram, Tiktok, Yelp, Facebook)
// are now exported from ./columns/socialMediaColumns.tsx

export const renderGoogleMapsWebsiteColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_website.website";

	return {
		title: ts("website"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "website",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				{row.mapBusiness?.website && (
					<Link href={ensureHttpProtocol(row.mapBusiness?.website)} target="_blank">
						{row.mapBusiness?.website}
					</Link>
				)}
			</div>
		),
	};
};

export const renderGoogleMapsCompanyCountryColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_business.companyCountry";

	return {
		title: ts("companyCountry"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "companyCountry",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				{row.prospect?.organization?.country || row.mapBusiness?.companyCountry}
			</div>
		),
	};
};

export const renderGoogleMapsCompanyStateColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_business.companyState";

	return {
		title: ts("companyState"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "companyState",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				{row.prospect?.organization?.state || row.mapBusiness?.companyState}
			</div>
		),
	};
};

export const renderGoogleMapsCompanyCityColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "map_business.companyCity";

	return {
		title: ts("companyCity"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "companyCity",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				{row.prospect?.organization?.city || row.mapBusiness?.companyCity}
			</div>
		),
	};
};

export const renderNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	/** Open the lead's detail panel — wired to the channel icons' "open panel" affordance. */
	onOpenDetail?: (row: ListPerson) => void
): TableColumn => {
	const orderTitle = "prospect.name";

	return {
		title: ts("name"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		locked: "leftFourth",
		icon: faLock,
		calcWidth: 260,
		moveable: false,
		hideable: true,
		minWidth: "260px",
		uid: "name",
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row gap-2">
				<LeadColorDots row={row} isMoreThanOne={true} />
				<EditableNameColumn
					{...{
						row,
						handleCopyClick,
						orderTitle,
						onOpenDetail,
					}}
				/>
			</div>
		),
	};
};

export const renderMapNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: ({ id, name }: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "map_business.name";

	return {
		title: ts("name"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		locked: "leftFourth",
		icon: faLock,
		moveable: false,
		hideable: true,
		minWidth: "180px",
		uid: "name",
		show: true,
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		render: (row: any) => (
			<EditableMapNameColumn
				{...{
					row,
					handleCopyClick,
					orderTitle,
					setShowEdit,
					showEdit,
					sortingInbox,
				}}
			/>
		),
	};
};

export const EditableJobNameColumn: FunctionComponent<{
	row: ListPerson;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const [value, setValue] = useState<string | undefined>(row?.prospect?.name || "");

	const [updateProspect, { isLoading }] = useUpdateProspectMutation();

	if (isLoading) {
		return <CircularProgress />;
	}

	return (
		<div className="relative flex w-full max-w-[180px] flew-row justify-between items-center">
			{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
				<div className="min-w-[180px] absolute z-10">
					<InputField
						autoFocus
						handleBlur={() => {
							setShowEdit({ id: 0, name: "" });
						}}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setValue(event.target.value);
						}}
						name={orderTitle}
						value={value || ""}
						onKeyDown={async (e) => {
							if (e.key === "Enter" && value) {
								await updateProspect({
									id: row.id,
									...{ name: value },
								}).unwrap();
								setShowEdit({ id: 0, name: "" });
							}
						}}
					/>
				</div>
			) : (
				<Tooltip content={row.prospect?.name || row.offer?.name}>
					<button
						className="cursor-pointer mr-1 max-w-[100px] text-ellipsis overflow-hidden"
						onClick={() =>
							(row.prospect?.name || row.offer?.name) &&
							handleCopyClick(row.prospect?.name || row.offer?.name || "")
						}
					>
						<HideOnErrorImg
							alt={row.prospect?.name ?? ""}
							className="w-[24px] h-[24px] rounded-full mr-1"
							referrerPolicy="no-referrer"
							src={row.prospect?.image}
						/>
						<span
							onContextMenu={(e) => {
								e.preventDefault(); // prevent the default behaviour when right clicked
								e.stopPropagation();
								setShowEdit({ id: row.id, name: orderTitle });
							}}
						>
							{row.prospect?.name || row.offer?.name}
						</span>
					</button>
				</Tooltip>
			)}
			<div className="flex flex-row">
				{row.prospect?.linkedinUrl || row.offer?.linkedinUrl ? (
					<Link
						href={ensureHttpProtocol(row.prospect?.linkedinUrl || row.offer?.linkedinUrl)}
						target="_blank"
					>
						<LinkedinIcon status="checked" />
					</Link>
				) : (
					(row.prospect?.name || row.offer?.name) && <LinkedinIcon status="failed" />
				)}

				{row.offer?.email && row.offer?.emailStatus === "VALID" && (
					<button
						aria-label="copy email"
						className="mr-1 cursor-pointer"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.offer?.email ?? "");
						}}
					>
						<EmailIcon status="checked" />
					</button>
				)}
				{row.offer?.email && row.offer?.emailStatus === "INVALID" && (
					<button
						aria-label="copy email"
						className="mr-1 cursor-pointer"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.offer?.email ?? "");
						}}
					>
						<EmailIcon status="failed" />
					</button>
				)}
				{row.offer?.email &&
					(row.offer?.emailStatus === "BOUNCED_VALID" ||
						row.offer?.emailStatus === "BOUNCED_INVALID") && (
						<button
							aria-label="copy email"
							className="mr-1 cursor-pointer"
							type="button"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(row.offer?.email ?? "");
							}}
						>
							<EmailIcon
								status={
									row.offer?.emailStatus === "BOUNCED_VALID" ? "bounced-valid" : "bounced-invalid"
								}
							/>
						</button>
					)}
				{(row.offer?.emailStatus === "RETRY" || !row.offer?.emailStatus) && (
					<OfferEmailStatusBadge
						email={row.offer?.email}
						onCopy={handleCopyClick}
						status={row.offer?.emailStatus === "RETRY" ? "retry" : "refresh"}
					/>
				)}
				{row.offer?.phoneNumber ? (
					<Tooltip content={row.offer?.phoneNumber || ""}>
						<button
							aria-label="copy phoneNumber"
							className="mr-1 cursor-pointer"
							type="button"
							onClick={() => handleCopyClick(row.offer?.phoneNumber || "")}
						>
							<PhoneIcon status="checked" />
						</button>
					</Tooltip>
				) : (
					<PhoneIcon status="failed" />
				)}
			</div>
		</div>
	);
};

export const renderCompanyCategoryColumn = (sortingInbox: SortParams): TableColumn => {
	const orderTitle = "score_company_category_result.result";

	return {
		title: "Company Segment",
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "companyCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			return <ModalChangeCompanyCategory listPerson={row} />;
		},
	};
};

export const renderCompanyScoreColumn = (sortingInbox: SortParams): TableColumn => {
	const orderTitle = "score_persona_category_result.result";

	return {
		title: "Company Priority",
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "companyScore",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			return (
				<div className="flex items-center">
					<Tooltip
						content={row.prospect?.scorePersonaCategoryResult?.companyScoringSummary}
						disabled={!row.prospect?.scorePersonaCategoryResult?.companyScoringSummary}
					>
						{row.prospect?.scorePersonaCategoryResult?.companyScore}
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderPersonaScoreColumn = (sortingInbox: SortParams): TableColumn => {
	const orderTitle = "score_persona_category_result.result";

	return {
		title: "Persona Priority",
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "personaScore",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			return (
				<div className="flex items-center">
					<Tooltip
						content={row.prospect?.scorePersonaCategoryResult?.personaScoringSummary}
						disabled={!row.prospect?.scorePersonaCategoryResult?.personaScoringSummary}
					>
						{row.prospect?.scorePersonaCategoryResult?.personaScore}
					</Tooltip>
				</div>
			);
		},
	};
};

export const renderPersonaCategoryColumn = (sortingInbox: SortParams): TableColumn => {
	const orderTitle = "score_persona_category_result.result";

	return {
		title: "Persona Segment",
		iconRender: () => {
			return <Icon className="ml-1 text-action-400" icon={faStar} />;
		},
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		allowExclude: true,
		uid: "personaCategory",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			return <ModalChangePersonaCategory listPerson={row} />;
		},
	};
};

export { renderCurrentScoreResult };

// Calculate combined tracker score based on signal priority and lead score matrix
const calculateTrackerScore = (
	signalPriority: string | null | undefined,
	leadScore: string | null | undefined
): string | null => {
	// Normalize signal priority (e.g., "Prio 1" -> "1")

	const normalizeSignal = (signal: string | null | undefined): string | null => {
		if (!signal) {
			return null;
		}

		if (signal === "Disqualified") {
			return "Disqualified";
		}

		const match = signal.match(/\d/);

		return match ? match[0] : null;
	};

	const normalizedSignal = normalizeSignal(signalPriority);
	const normalizedLead = leadScore;

	// If either is Disqualified, combined is Disqualified
	if (normalizedSignal === "Disqualified" || normalizedLead === "Disqualified") {
		return "Disqualified";
	}

	// If only one exists, return that one
	if (!normalizedSignal && normalizedLead) {
		return normalizedLead;
	}

	if (normalizedSignal && !normalizedLead) {
		return normalizedSignal;
	}

	if (!normalizedSignal && !normalizedLead) {
		return null;
	}

	// Both exist - use matrix
	const signal = parseInt(normalizedSignal!);
	const lead = parseInt(normalizedLead!);

	const matrix: Record<number, Record<number, number>> = {
		1: { 3: 2, 2: 1, 1: 1 }, // Signal P1 + Lead P3/P2/P1 => P2/P1/P1
		2: { 3: 3, 2: 2, 1: 1 }, // Signal P2 + Lead P3/P2/P1 => P3/P2/P1
		3: { 3: 3, 2: 3, 1: 2 }, // Signal P3 + Lead P3/P2/P1 => P3/P3/P2
	};

	const result = matrix[signal]?.[lead];

	return result ? String(result) : null;
};

// Get display score (P1, P2, P3, DQ, ?)
const getDisplayScore = (score: string | null): string => {
	if (!score) {
		return "?";
	}

	if (score === "Disqualified") {
		return "DQ";
	}

	if (score === "1") {
		return "P1";
	}

	if (score === "2") {
		return "P2";
	}

	if (score === "3") {
		return "P3";
	}

	return "?";
};

export const RenderTrackerOverallScore: FunctionComponent<{
	scoring?: PersonaCategoryResults | null;
	companyCategory?: CompanyCategoryResults | null;
	listPerson: ListPerson;
	trackerSignalResultId?: number | null;
	signalPriority?: string | null;
	signalReasoning?: string | null;
}> = ({
	scoring,
	companyCategory: _companyCategory, // eslint-disable-line @typescript-eslint/no-unused-vars
	listPerson,
	trackerSignalResultId,
	signalPriority,
	signalReasoning,
}) => {
	const { data: score } = useGetScoreQuery();

	// Local state for editing
	const [localSignalPriority, setLocalSignalPriority] = useState<string | null>(
		signalPriority || null
	);
	const [localOverallScore, setLocalOverallScore] = useState<string | null>(
		scoring?.overallScore || null
	);

	// Calculate combined score using local state values so it updates after save
	const combinedScore = calculateTrackerScore(localSignalPriority, localOverallScore);
	const displayScore = getDisplayScore(combinedScore);

	const [selectedPersonaCategory, setSelectedPersonaCategory] = useState<{
		id: number;
		title: string;
	} | null>();

	const [selectedCompanyCategory, setSelectedCompanyCategory] = useState<{
		id: number;
		title: string;
	} | null>();

	useEffect(() => {
		if (
			listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory ||
			listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result
		) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSelectedCompanyCategory({
				id:
					listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory
						?.id || 0,
				title:
					listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.scoreCompanyCategory
						?.category ||
					listPerson?.prospect?.organization?.scoreCompanyCategoryResult?.result ||
					"",
			});
		}

		if (
			listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory ||
			listPerson?.prospect?.scorePersonaCategoryResult?.result
		) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSelectedPersonaCategory({
				id: listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.id || 0,
				title:
					listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.category ||
					listPerson?.prospect?.scorePersonaCategoryResult?.result ||
					"",
			});
		}
	}, [listPerson]);

	const [isVisible, setIsVisible] = useState(false);
	const [reason, setReason] = useState("");
	const [updateLeadScoringResult, { isLoading }] = useUpdateLeadScoringResultMutation();
	const [updateSignalPriority] = useUpdateSignalPriorityMutation();

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		// Update signal priority if changed and trackerSignalResultId exists
		if (localSignalPriority && localSignalPriority !== signalPriority && trackerSignalResultId) {
			await updateSignalPriority({ trackerSignalResultId, priority: localSignalPriority }).unwrap();
		}

		// No grade picked writes no lead score: a missing score is not a disqualification (PREC-4075).
		if (localOverallScore) {
			await updateLeadScoringResult({
				id: listPerson.id,
				overallScore: localOverallScore,
				scorePersonaCategoryId: selectedPersonaCategory?.id || 0,
				personaResult: selectedPersonaCategory?.title || "",
				scoreCompanyCategoryId: selectedCompanyCategory?.id || 0,
				companyResult: selectedCompanyCategory?.title || "",
				reason,
			}).unwrap();
		}

		setIsVisible(false);
		setReason("");
	};

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useAnchoredTooltip(triggerRef, tooltipRef, isVisible);

	// Normalize signal priority for display ("Prio 1" -> "1")
	const normalizeSignalForState = (signal: string | null | undefined): string | null => {
		if (!signal) {
			return null;
		}

		if (signal === "Disqualified") {
			return "Disqualified";
		}

		const match = signal.match(/\d/);

		return match ? match[0] : null;
	};

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[320px]"
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				{/* Signal Priority Section */}
				<div className="flex flex-col gap-2">
					<div className="text-sm font-semibold text-gray-700">Signal Priority</div>
					<div className="flex flex-row gap-2">
						<div
							className={classNames(
								"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-success border font-bold bg-background-success rounded-full justify-center items-center",
								normalizeSignalForState(localSignalPriority) === "1"
									? "border-blue-action border-2"
									: "border-text-success"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Prio 1");
							}}
						>
							S1
						</div>
						<div
							className={classNames(
								"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-warning border font-bold bg-background-warning rounded-full justify-center items-center",
								normalizeSignalForState(localSignalPriority) === "2"
									? "border-blue-action border-2"
									: "border-text-warning"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Prio 2");
							}}
						>
							S2
						</div>
						<div
							className={classNames(
								"cursor-pointer w-[24px] h-[24px] border text-error text-xxs font-bold bg-error-light rounded-full justify-center items-center flex",
								normalizeSignalForState(localSignalPriority) === "3"
									? "border-blue-action border-2"
									: "border-error"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Prio 3");
							}}
						>
							S3
						</div>
						<div
							className={classNames(
								"cursor-pointer w-[24px] h-[24px] text-white text-xxs font-bold bg-error rounded-full justify-center items-center flex",
								localSignalPriority === "Disqualified"
									? "border-blue-action border-2"
									: "border-error"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Disqualified");
							}}
						>
							DQ
						</div>
					</div>
				</div>

				{/* Lead Score Section */}
				<div className="flex flex-col gap-2">
					<div className="text-sm font-semibold text-gray-700">Lead Score</div>
					<div className="flex flex-row gap-2">
						<div
							className={classNames(
								"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-success border font-bold bg-background-success rounded-full justify-center items-center",
								localOverallScore === "1" ? "border-blue-action border-2" : "border-text-success"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalOverallScore("1");
							}}
						>
							P1
						</div>
						<div
							className={classNames(
								"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-warning border font-bold bg-background-warning rounded-full justify-center items-center",
								localOverallScore === "2" ? "border-blue-action border-2" : "border-text-warning"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalOverallScore("2");
							}}
						>
							P2
						</div>
						<div
							className={classNames(
								"cursor-pointer w-[24px] h-[24px] border text-error text-xxs font-bold bg-error-light rounded-full justify-center items-center flex",
								localOverallScore === "3" ? "border-blue-action border-2" : "border-error"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalOverallScore("3");
							}}
						>
							P3
						</div>
						<div
							className={classNames(
								"cursor-pointer w-[24px] h-[24px] text-white text-xxs font-bold bg-error rounded-full justify-center items-center flex",
								localOverallScore === "Disqualified"
									? "border-blue-action border-2"
									: "border-error"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalOverallScore("Disqualified");
							}}
						>
							DQ
						</div>
					</div>
				</div>

				{/* Category Dropdowns */}
				<div className="flex flex-col gap-2">
					<div>
						<Dropdown
							data={[
								...(score?.prospectCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString()})`,
								})) || []),
								...(score?.partnerCompanyCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category} (${category.type?.capitalizeString()})`,
								})) || []),
								{
									id: 0,
									title: "Disqualified",
								},
								{
									id: 0,
									title: "Competitor",
								},
							]}
							defaultValue={
								selectedCompanyCategory
									? {
											id: selectedCompanyCategory.id,
											title: selectedCompanyCategory.title,
										}
									: undefined
							}
							portal={true}
							handleSelect={(selected) => {
								setSelectedCompanyCategory({
									id: selected.id as number,
									title: selected.title,
								});
							}}
							label="Company category"
						/>
					</div>
					<div>
						<Dropdown
							data={[
								...(score?.prospectPersonaCategories?.map((category) => ({
									id: category.id || 0,
									title: `${category.category}` || "",
								})) || []),
								{
									id: 0,
									title: "Disqualified",
								},
							]}
							defaultValue={
								selectedPersonaCategory
									? {
											id: selectedPersonaCategory.id,
											title: selectedPersonaCategory.title,
										}
									: undefined
							}
							portal={true}
							handleSelect={(selected) => {
								setSelectedPersonaCategory({
									id: selected.id as number,
									title: selected.title,
								});
							}}
							label="Persona category"
						/>
					</div>
				</div>

				{/* Reason */}
				<div>
					<TextArea
						handleChange={(e) => setReason(e.target.value)}
						label="Reason for change"
						name="reason"
						placeholder="Enter reason for this change..."
						showError={false}
						value={reason}
					/>
				</div>

				{/* Save Button */}
				<div className="flex flex-row gap-2 justify-end">
					<Button
						color={ButtonColor.ACTION}
						title="Save"
						disabled={isLoading}
						onClick={handleSave}
					/>
				</div>
			</div>,
			document.body
		);
	};

	// Generate tooltip content showing both scores
	const generateTooltipContent = () => {
		const signalDisplay = localSignalPriority || "Not set";
		const leadDisplay = toLeadScoreText(localOverallScore);
		// A "?" lead's reason is what says what to configure, so it is shown beside a signal's.
		const unsureReason = leadDisplay === "?" ? scoring?.overallReasoning : null;
		const reasoningHtml =
			[signalReasoning, unsureReason].filter(Boolean).join("<br />") ||
			scoring?.overallReasoning ||
			"";

		return `
			<div>
				<div><strong>Signal:</strong> ${signalDisplay}</div>
				<div><strong>Lead:</strong> ${leadDisplay}</div>
				${reasoningHtml ? `<div class="mt-2">${reasoningHtml}</div>` : ""}
			</div>
		`;
	};

	return (
		<>
			{isVisible && renderChangeResult()}
			<Tooltip
				content={
					// eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
					<div dangerouslySetInnerHTML={{ __html: generateTooltipContent() }} />
				}
				delay={300}
			>
				<div
					ref={triggerRef}
					className={classNames(
						"cursor-pointer w-[24px] h-[24px] text-xxs font-bold rounded-full justify-center items-center flex",
						displayScore === "?"
							? "text-text-meta bg-border-light"
							: displayScore === "DQ"
								? "text-white bg-error"
								: displayScore === "P1"
									? "text-text-success border border-text-success bg-background-success"
									: displayScore === "P2"
										? "text-text-warning border border-text-warning bg-background-warning"
										: "text-error border border-error bg-error-light"
					)}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsVisible(!isVisible);
					}}
				>
					{displayScore}
				</div>
			</Tooltip>
		</>
	);
};

export const EditableMapNameColumn: FunctionComponent<{
	row: ListPerson;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const [value, setValue] = useState<string | undefined>(row?.mapBusiness?.name || "");

	const [updateMapBusiness, { isLoading }] = useUpdateMapBusinessMutation();

	if (isLoading) {
		return <CircularProgress />;
	}

	return (
		<div className="relative flex w-full max-w-[180px] flew-row justify-between items-center">
			{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
				<div className="min-w-[180px] absolute z-10">
					<InputField
						autoFocus
						handleBlur={() => {
							setShowEdit({ id: 0, name: "" });
						}}
						handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
							setValue(event.target.value);
						}}
						name={orderTitle}
						value={value || ""}
						onKeyDown={async (e) => {
							if (e.key === "Enter" && value) {
								await updateMapBusiness({
									id: row.id,
									...{ name: value },
								}).unwrap();
								setShowEdit({ id: 0, name: "" });
							}
						}}
					/>
				</div>
			) : (
				<Tooltip content={row.mapBusiness?.name}>
					<button
						className="cursor-pointer mr-1 max-w-[100px] text-ellipsis overflow-hidden"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							if (row.prospect?.name) {
								handleCopyClick(row.mapBusiness?.name || "");
							}
						}}
					>
						<span
							onContextMenu={(e) => {
								e.preventDefault(); // prevent the default behaviour when right clicked
								e.stopPropagation();
								setShowEdit({ id: row.id, name: orderTitle });
							}}
						>
							{row.mapBusiness?.name}
						</span>
					</button>
				</Tooltip>
			)}
			<div className="flex flex-row">
				{row.mapBusiness?.linkedinUrl ? (
					<Link href={ensureHttpProtocol(row.mapBusiness?.linkedinUrl)} target="_blank">
						<LinkedinIcon status="checked" />
					</Link>
				) : (
					row.mapBusiness?.name && <LinkedinIcon status="failed" />
				)}

				{row.mapBusiness?.email && (
					<button
						aria-label="copy email"
						className="mr-1 cursor-pointer"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.mapBusiness?.email ?? "");
						}}
					>
						<EmailIcon status="checked" />
					</button>
				)}
				{!row.mapBusiness?.email && (
					<button aria-label="copy email" className="mr-1 cursor-pointer" type="button">
						<EmailIcon status="failed" />
					</button>
				)}
				{row.mapBusiness?.phoneNumber ? (
					<Tooltip content={row.mapBusiness?.phoneNumber || ""}>
						<button
							aria-label="copy phoneNumber"
							className="mr-1 cursor-pointer"
							type="button"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(row.mapBusiness?.phoneNumber || "");
							}}
						>
							<PhoneIcon status="checked" />
						</button>
					</Tooltip>
				) : (
					<PhoneIcon status="failed" />
				)}
			</div>
		</div>
	);
};

export const EditableCompanyColumn: FunctionComponent<{
	row: ListPerson;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const [value, setValue] = useState<string | undefined>(row?.prospect?.organization?.name || "");

	const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();

	const currentCompanyUrl = useMemo(() => {
		const url =
			row.prospect?.organization?.websiteUrl ??
			row.prospect?.organization?.primaryDomain ??
			undefined;

		return url ? ensureHttpProtocol(url) : undefined;
	}, [row.prospect?.organization?.websiteUrl, row.prospect?.organization?.primaryDomain]);

	if (isLoading) {
		return <CircularProgress />;
	}

	return (
		<div className="flex w-full max-w-[270px] items-center">
			<HideOnErrorImg
				alt="logo"
				className="max-h-[24px] h-full w-[24px] rounded mr-1"
				referrerPolicy="no-referrer"
				src={row.prospect?.organization?.logoUrl}
			/>
			<div className="ml-2 w-full flex flex-row justify-between">
				{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
					<div className="min-w-[260px] absolute left-0 top-0 z-10">
						<InputField
							autoFocus
							handleBlur={() => {
								setShowEdit({ id: 0, name: "" });
							}}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue(event.target.value);
							}}
							name={orderTitle}
							value={value || ""}
							onKeyDown={async (e) => {
								if (e.key === "Enter" && value) {
									await updateOrganization({
										id: row.id,
										...{ name: value },
									}).unwrap();
									setShowEdit({ id: 0, name: "" });
								}
							}}
						/>
					</div>
				) : (
					<Tooltip
						content={
							<InfoCardPopover
								kind="company"
								onCopy={handleCopyClick}
								rows={
									[
										{ label: "Name", value: row.prospect?.organization?.name },
										{ label: "Location", value: row.prospect?.organization?.country },
										{
											label: "Employees",
											value: formatEmployees(
												row.prospect?.organization?.employeesFrom,
												row.prospect?.organization?.employeesTo
											),
										},
										{
											label: "Company category",
											value: row.prospect?.organization?.scoreCompanyCategoryResult?.result,
										},
										{
											label: "Company prio",
											value: row.prospect?.scorePersonaCategoryResult?.companyScore,
										},
										{
											label: "Short description",
											value: row.prospect?.organization?.shortDescription,
											long: true,
										},
									] satisfies InfoCardPopoverRow[]
								}
							/>
						}
						exitDelay={100}
					>
						<button
							className="cursor-pointer mr-1 min-w-0 flex-1 max-w-[140px] overflow-hidden block text-left"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(row.prospect?.organization?.name || "");
							}}
						>
							<span
								className="block truncate rounded-[5px] px-[3px] py-[1px] -mx-[3px] -my-[1px] hover:bg-gray-50 transition-colors"
								onContextMenu={(e) => {
									e.stopPropagation();
									e.preventDefault(); // prevent the default behaviour when right clicked
									setShowEdit({ id: row.id, name: orderTitle });
								}}
							>
								{row.prospect?.organization?.name || ""}
							</span>
						</button>
					</Tooltip>
				)}

				<div className="flex items-center">
					{row.prospect?.organization?.crmOrganizations?.map((crmOrganization) => (
						<Tooltip
							key={crmOrganization.id}
							content={
								<div className="flex flex-col gap-2">
									{crmOrganization.lastUpdatedAt ? (
										<span>
											Last Updated At:{" "}
											{format(new Date(crmOrganization.lastUpdatedAt), "HH:mm dd.MM.yyyy")}
										</span>
									) : (
										<></>
									)}
									<span>Account Owner: {crmOrganization.accountOwner}</span>
									<span>Account Owner ID: {crmOrganization.accountOwnerId}</span>
									<span>Reference ID: {crmOrganization.referenceId}</span>
									{crmOrganization.customField1 && (
										<span>Custom Field 1: {crmOrganization.customField1}</span>
									)}
									{crmOrganization.customField2 && (
										<span>Custom Field 2: {crmOrganization.customField2}</span>
									)}
								</div>
							}
							disabled={!crmOrganization.url}
						>
							<Link href={ensureHttpProtocol(crmOrganization.url)} target="_blank">
								<CrmIcon url={crmOrganization.url ?? ""} />
							</Link>
						</Tooltip>
					))}

					{row.prospect?.organization?.linkedinUrl ? (
						<Link
							href={ensureHttpProtocol(row.prospect?.organization?.linkedinUrl)}
							target="_blank"
						>
							<LinkedinIcon status="checked" />
						</Link>
					) : (
						row.prospect?.organization && <LinkedinIcon status="failed" />
					)}

					{currentCompanyUrl ? (
						<Link href={currentCompanyUrl || ""} target="_blank">
							<WebsiteIcon status="checked" />
						</Link>
					) : (
						<></>
					)}
				</div>
			</div>
		</div>
	);
};
export const renderCreatedAt = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.createdAt";

	return {
		title: ts("createdAt"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "createdAt",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex items-center">
				{row.createdAt ? format(new Date(row.createdAt), "yyyy-MM-dd HH:mm") : ""}
			</div>
		),
	};
};

export const renderCampaignAddedAt = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.addedToCampaignAt";

	return {
		title: ts("addedToCampaignAt"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "addedToCampaignAt",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex items-center">
				{row.addedToCampaignAt ? format(new Date(row.addedToCampaignAt), "yyyy-MM-dd HH:mm") : ""}
			</div>
		),
	};
};

export const renderAssignedStatusColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.assignStatus";

	return {
		title: ts("assignedStatus"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "assignedStatus",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex items-center">
				{row.assignStatus ? ts(`assignStatus.${row.assignStatus}`) : ""}
			</div>
		),
	};
};

// Assigned-tab column: shows whether the lead currently sits in Inbox or Outbox and links
// to that view (pre-filtered by the lead's list) so the user can open the conversation there.
export const renderInboxOutboxColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.assignStatus";

	return {
		title: ts("inboxOutbox"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "inboxOutbox",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			const { kind, to } = resolveLeadLocation({
				assignStatus: row.assignStatus,
				listId: row.list?.id,
				listName: row.list?.name,
				prospectName: row.prospect?.name,
			});

			if (kind !== "INBOX" && kind !== "OUTBOX") {
				return <div className="flex items-center" />;
			}

			return (
				<div className="flex items-center gap-2">
					<span className="rounded-full bg-status-info-light px-2 py-0.5 text-xs font-semibold text-status-info">
						{ts(kind === "INBOX" ? "locationInbox" : "locationOutbox")}
					</span>
					<Link className="text-sm text-status-info hover:underline" href={to} target="_blank">
						{ts("openChat")}
					</Link>
				</div>
			);
		},
	};
};

export const renderLastNodeTypeColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.lastNodeType";

	return {
		title: ts("lastNodeType"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "lastNodeType",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex items-center">
				{row.lastNodeType ? ts(`nodeType.${row.lastNodeType}`) : ""}
			</div>
		),
	};
};

export const renderLastScheduledAtColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.lastScheduledAt";

	return {
		title: ts("lastScheduledAt"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "lastScheduledAt",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex items-center">
				{row.lastScheduledAt ? format(new Date(row.lastScheduledAt), "yyyy-MM-dd HH:mm") : ""}
			</div>
		),
	};
};

export const renderLastMessageColumn = (ts: (key: string) => string): TableColumn => {
	return {
		title: ts("lastMessage"),
		width: "fit-content",
		uid: "lastMessage",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			const conversations = row.prospect?.conversations;

			if (!conversations || conversations.length === 0) {
				return <span className="text-text-disabled">-</span>;
			}

			const sentConversations = conversations
				.filter((c) => !c.scheduled && c.listPersonId === row.id)
				.sort((a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime());

			const lastMessage = sentConversations[0]?.message;

			return <TruncatedText maxWidth={250} value={lastMessage} />;
		},
	};
};

export const EditableJobsCompanyColumn: FunctionComponent<{
	row: ListPerson;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit }) => {
	const [value, setValue] = useState<string | undefined>(row?.prospect?.organization?.name || "");

	const [updateOrganization, { isLoading }] = useUpdateOrganizationMutation();

	if (isLoading) {
		return <CircularProgress />;
	}

	return (
		<div className="flex w-full max-w-[220px] items-center">
			<div className="ml-2 w-full flex flex-row justify-between">
				{showEdit && showEdit?.id === row.id && showEdit?.name === orderTitle ? (
					<div className="min-w-[180px] absolute left-0 top-0 z-10">
						<InputField
							autoFocus
							handleBlur={() => {
								setShowEdit({ id: 0, name: "" });
							}}
							handleChange={function (event: ChangeEvent<HTMLInputElement>): void {
								setValue(event.target.value);
							}}
							name={orderTitle}
							value={value || ""}
							onKeyDown={async (e) => {
								if (e.key === "Enter" && value) {
									await updateOrganization({
										id: row.id,
										...{ name: value },
									}).unwrap();
									setShowEdit({ id: 0, name: "" });
								}
							}}
						/>
					</div>
				) : (
					<Tooltip
						content={
							<div className="flex flex-col min-w-[300px]">
								<div>
									<span className="font-bold">Name:</span>
									{row.offer?.companyName}
								</div>
							</div>
						}
					>
						<button
							className="cursor-pointer mr-1  max-w-[100px] text-ellipsis overflow-hidden"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(row.offer?.companyName || "");
							}}
						>
							<span
								onContextMenu={(e) => {
									e.stopPropagation();
									e.preventDefault(); // prevent the default behaviour when right clicked
									setShowEdit({ id: row.id, name: orderTitle });
								}}
							>
								{row.offer?.companyName || ""}
							</span>
						</button>
					</Tooltip>
				)}

				<div className="flex items-center">
					{row.offer?.companyEmail ? (
						<Link href={`mailto: ${row.offer?.companyEmail || ""}`} target="_blank">
							<EmailIcon status="checked" />
						</Link>
					) : (
						<EmailIcon status="failed" />
					)}
					{row.offer?.companyLinkedinUrl ? (
						<Link href={ensureHttpProtocol(row.offer?.companyLinkedinUrl)} target="_blank">
							<LinkedinIcon status="checked" />
						</Link>
					) : (
						<LinkedinIcon status="failed" />
					)}
					{row.offer?.companyDomain && (
						<Link href={ensureHttpProtocol(row.offer.companyDomain)} target="_blank">
							<ExternalLink />
						</Link>
					)}
				</div>
			</div>
		</div>
	);
};

export const renderJobsNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: ({ id, name }: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "offer.name";

	return {
		title: ts("name"),
		width: "fit-content",
		orderTitle,
		locked: "leftFourth",
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		icon: faLock,
		moveable: false,
		hideable: true,
		minWidth: "180px",
		uid: "name",
		show: true,
		render: (row: ListPerson) => (
			<EditableJobNameColumn
				{...{
					row,
					handleCopyClick,
					orderTitle,
					setShowEdit,
					showEdit,
					sortingInbox,
				}}
			/>
		),
	};
};

// Salutation columns (DE/EN for prospect, job, and map)
// are now exported from ./columns/salutationColumns.tsx

const GENDER_OPTIONS = [
	{ id: "male", title: "Male" },
	{ id: "female", title: "Female" },
	{ id: "", title: "— Clear —" },
];

const GENDER_CONFIRM_OPTION_ID = "__confirm__";

export const EditableGenderColumn: FunctionComponent<{
	row: ListPerson;
	handleCopyClick: (value: string) => void;
	setShowEdit: ({ id, name }: { id: number; name: string }) => void;
	showEdit: { id: number; name: string } | undefined;
	orderTitle: string;
	ts: (key: string) => string;
}> = ({ row, handleCopyClick, orderTitle, setShowEdit, showEdit, ts }) => {
	const dispatch = useAppDispatch();
	const [updateProspect, { isLoading }] = useUpdateProspectMutation();

	if (isLoading) {
		return <CircularProgress size={16} />;
	}

	const gender = row.prospect?.gender ?? null;
	const genderProbability = row.prospect?.genderProbability ?? null;
	const confidence = getGenderConfidenceState(gender, genderProbability);
	const isEditing = showEdit && showEdit.id === row.id && showEdit.name === orderTitle;

	const dropdownOptions =
		confidence === "unsure" && gender
			? [{ id: GENDER_CONFIRM_OPTION_ID, title: ts("genderConfirmOption") }, ...GENDER_OPTIONS]
			: GENDER_OPTIONS;

	const handleDropdownSelect = async (selected: { id: number | string | null }) => {
		if (!row.prospect?.id) {
			setShowEdit({ id: 0, name: "" });

			return;
		}

		const nextGender =
			selected.id === GENDER_CONFIRM_OPTION_ID
				? gender
				: selected.id === ""
					? null
					: (selected.id as string);

		await updateProspect({
			id: row.id,
			...{ gender: nextGender },
		}).unwrap();

		dispatch(updateProspectGenderInInbox({ listPersonId: row.id, gender: nextGender }));

		setShowEdit({ id: 0, name: "" });
	};

	const renderIndicator = () => {
		if (confidence === "confirmed") {
			return (
				<Tooltip content={ts("genderConfirmedTooltip")}>
					<Icon className="text-status-success h-4 w-4" icon={faCheckCircle} />
				</Tooltip>
			);
		}

		if (confidence === "unsure") {
			return (
				<Tooltip content={ts("genderUnsureTooltip")}>
					<button
						className="cursor-pointer flex items-center"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							setShowEdit({ id: row.id, name: orderTitle });
						}}
					>
						<Icon className="text-status-warning h-4 w-4" icon={faWarning} />
					</button>
				</Tooltip>
			);
		}

		if (confidence === "empty") {
			return (
				<Tooltip content={ts("genderSetTooltip")}>
					<button
						className="cursor-pointer flex items-center"
						type="button"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							setShowEdit({ id: row.id, name: orderTitle });
						}}
					>
						<Icon className="text-text-meta h-4 w-4" icon={faPlus} />
					</button>
				</Tooltip>
			);
		}

		return null;
	};

	return (
		<div className="relative flex w-full min-w-[100px] max-w-[250px] flex-row justify-between items-center gap-1">
			{isEditing ? (
				<div className="min-w-[80px] absolute z-10 flex flex-row items-center gap-1">
					<Dropdown
						data={dropdownOptions}
						defaultValue={gender ? GENDER_OPTIONS.find((o) => o.id === gender) : undefined}
						floating={true}
						handleSelect={handleDropdownSelect}
						portal={true}
						size={ButtonSize.XS}
					/>
					<Tooltip content={ts("genderCancelTooltip")}>
						<button
							className="cursor-pointer flex items-center text-text-meta hover:text-status-error"
							type="button"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								setShowEdit({ id: 0, name: "" });
							}}
						>
							<Icon className="h-4 w-4" icon={faXmark} />
						</button>
					</Tooltip>
				</div>
			) : (
				<>
					<Tooltip content={ts("genderRightClickTooltip")}>
						<button
							className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								handleCopyClick(gender ?? "");
							}}
							onContextMenu={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setShowEdit({ id: row.id, name: orderTitle });
							}}
						>
							{gender?.capitalizeString()}
						</button>
					</Tooltip>
					{renderIndicator()}
				</>
			)}
		</div>
	);
};

export const renderGenderColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams,
	setShowEdit: ({ id, name }: { id: number; name: string }) => void,
	showEdit: { id: number; name: string } | undefined
): TableColumn => {
	const orderTitle = "prospect.gender";

	return {
		title: ts("gender"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "gender",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<EditableGenderColumn
				{...{
					row,
					handleCopyClick,
					orderTitle,
					setShowEdit,
					showEdit,
					ts,
				}}
			/>
		),
	};
};

// The Reason column moved to ./columns/exclusionReasonColumn (extended with the recoverable
// "Title excluded" tag, PREC-3536 Phase 5); the Enriched-at column lives beside it. Both are
// re-exported here so the TableColumnConfigs imports keep working.
export {
	renderExclusionReasonColumn,
	type ExclusionReasonColumnActions,
} from "./columns/exclusionReasonColumn";
export { renderEnrichedAtColumn } from "./columns/enrichedAtColumn";

export const renderSignalScoreColumn = (): TableColumn => {
	return {
		title: "",
		width: "fit-content",
		allowExclude: false,
		uid: "signalScore",
		locked: "leftSecond",
		hideable: false,
		moveable: false,
		calcWidth: 40,
		show: true,
		render: (row: ListPerson) => {
			return (
				<div className="relative flex justify-center items-center">
					<RenderSignalScore
						listPerson={row}
						signalPriority={row.trackerSignalResult?.priority}
						signalReasoning={row.trackerSignalResult?.reasoning}
						trackerSignalResultId={row.trackerSignalResult?.id}
					/>
				</div>
			);
		},
	};
};

export const renderSelectColumn = (
	setShowAll: Dispatch<SetStateAction<boolean>>,
	toggleSelectAll: () => void,
	selectAll: boolean,
	selectedItems: number[],
	handleSelectItem: (id: number) => void
): TableColumn => {
	return {
		title: "",
		uid: "select",
		icon: selectAll ? faSquareCheck : faSquare,
		allowExclude: false,
		calcWidth: 62.5,
		locked: "leftFirst",
		hideable: false,
		moveable: false,
		show: true,
		onHeaderClick: () => {
			if (selectAll) {
				toggleSelectAll();
			} else {
				setShowAll(true);
			}
		},
		render: (row: ListPerson, index?: number) => (
			<div className="group flex items-center">
				<div
					className={classNames(
						"w-[16px] text-center font-medium text-table-header",
						selectedItems?.find((lf) => lf === row.id) ? "hidden" : "group-hover:hidden"
					)}
				>
					{typeof index !== "undefined" ? index + 1 : ""}
				</div>

				<Checkbox
					className={classNames(
						selectedItems?.find((lf) => lf === row.id) ? "block" : "hidden group-hover:block"
					)}
					isChecked={!!selectedItems?.find((lf) => lf === row.id)}
					name={"selectedProspects"}
					onChange={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();

						handleSelectItem(row.id);
					}}
				/>
			</div>
		),
	};
};

// The Title column moved to ./columns/titleColumn — it now renders and sorts on the same
// `prospect.title` every lead detail view shows (PREC-3804). Re-exported so importers keep working.
export { renderTitleColumn } from "./columns/titleColumn";

export const renderSourceNameColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "list_person.sourceName";

	return {
		title: ts("sourceName"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "sourceName",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div>
				<Tooltip content={row.sourceName}>
					<button
						className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.sourceName || "");
						}}
					>
						{row.sourceName}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderSeniorityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "job_title_to_seniority.seniority";

	return {
		title: ts("level"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "seniority",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<ModalChangeSeniority handleCopyClick={handleCopyClick} listPerson={row} row={row} />
		),
	};
};

export const renderContentTypeColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.type";

	return {
		title: ts("type"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "type",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				{row.liProfileEntryResult?.type && ts(row.liProfileEntryResult.type)}
			</div>
		),
	};
};

export const renderLiPostContentTypeColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.type";

	return {
		title: ts("type"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "type",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				{row.liPostResult?.type && ts(row.liPostResult?.type)}
			</div>
		),
	};
};

export const renderLiPostAuthorColumn = (
	_ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.authorName";

	return {
		title: "Author Name",
		width: "fit-content",
		minWidth: 200,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "authorName",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) =>
			row.liPostResult?.authorName ? (
				<div className="relative flex w-full max-w-[250px] flew-row justify-between items-center">
					<Tooltip content={row.liPostResult.authorName}>
						<button
							className="cursor-pointer mr-1 max-w-[170px] text-ellipsis overflow-hidden"
							onClick={(e: ReactMouseEvent) => {
								e.preventDefault();
								e.stopPropagation();
								if (row.liPostResult?.authorName) {
									handleCopyClick(row.liPostResult?.authorName || "");
								}
							}}
						>
							<HideOnErrorImg
								alt={row.liPostResult?.authorName ?? ""}
								className="w-[24px] h-[24px] float-left rounded-full mr-1"
								referrerPolicy="no-referrer"
								src={row.liPostResult?.authorImage}
							/>

							{row.liPostResult?.authorName ?? ""}
						</button>
					</Tooltip>
					<div className="flex flex-row">
						{row.liPostResult?.authorLinkedinUrl ? (
							<Link href={ensureHttpProtocol(row.liPostResult?.authorLinkedinUrl)} target="_blank">
								<LinkedinIcon status="checked" />
							</Link>
						) : row.liPostResult?.authorName ? (
							<LinkedinIcon status="failed" />
						) : (
							<></>
						)}
					</div>
				</div>
			) : (
				<></>
			),
	};
};

export const renderLiPostAuthorCompanyColumn = (
	_ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.authorCompanyName";

	return {
		title: "Author Company",
		width: "fit-content",
		minWidth: 200,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "authorCompanyName",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="relative flex w-full max-w-[250px] flew-row justify-between items-center">
				<Tooltip content={row.liPostResult?.authorCompanyName}>
					<button
						className="cursor-pointer mr-1 max-w-[170px] text-ellipsis overflow-hidden"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							if (row.liPostResult?.authorCompanyName) {
								handleCopyClick(row.liPostResult?.authorCompanyName || "");
							}
						}}
					>
						<HideOnErrorImg
							alt={row.liPostResult?.authorCompanyName ?? ""}
							className="w-[24px] h-[24px] float-left rounded-full mr-1"
							referrerPolicy="no-referrer"
							src={row.liPostResult?.authorCompanyImage}
						/>
						{row.liPostResult?.authorCompanyName}
					</button>
				</Tooltip>
				<div className="flex flex-row">
					{row.liPostResult?.authorCompanyLinkedinUrl ? (
						<Link
							href={ensureHttpProtocol(row.liPostResult?.authorCompanyLinkedinUrl)}
							target="_blank"
						>
							<LinkedinIcon status="checked" />
						</Link>
					) : (
						row.liPostResult?.authorCompanyName && <LinkedinIcon status="failed" />
					)}
				</div>
			</div>
		),
	};
};

export const renderLiPostContentColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.content";

	function truncate(str: string, n: number) {
		return str.length > n ? str.slice(0, n - 1) + "..." : str;
	}

	return {
		title: ts("content"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "content",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[200px] items-center">
				{row.liPostResult?.contentUrl && (
					<Link href={ensureHttpProtocol(row.liPostResult?.contentUrl)} target="_blank">
						<ExternalLink />
					</Link>
				)}
				<Tooltip
					content={
						<div className="flex flex-col min-w-[300px] text-sm">
							<div>
								<span
									className="leading-6 truncate ..."
									style={{
										textWrap: "wrap",
									}}
								>
									{truncate(row.liPostResult?.content ?? "", 180)}
								</span>
							</div>
						</div>
					}
				>
					<button
						className="text-ellipsis overflow-hidden max-w-[180px] cursor-pointer"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.liPostResult?.content ?? "");
						}}
					>
						{row.liPostResult?.content}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderLiPostContentResponseColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.contentResponse";

	function truncate(str: string, n: number) {
		return str.length > n ? str.slice(0, n - 1) + "..." : str;
	}

	return {
		title: ts("contentResponse"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contentResponse",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[200px] items-center">
				{row.liPostResult?.contentResponseUrl && (
					<Link href={ensureHttpProtocol(row.liPostResult?.contentResponseUrl)} target="_blank">
						<ExternalLink />
					</Link>
				)}
				<Tooltip
					content={
						<div className="flex flex-col min-w-[300px] text-sm">
							<div>
								<span
									className="leading-6 truncate ..."
									style={{
										textWrap: "wrap",
									}}
								>
									{truncate(row.liPostResult?.contentResponse ?? "", 180)}
								</span>
							</div>
						</div>
					}
				>
					<button
						className="text-ellipsis overflow-hidden max-w-[180px] cursor-pointer"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.liPostResult?.contentResponse ?? "");
						}}
					>
						{row.liPostResult?.contentResponse}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderLiProfileContentColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_profile_entry_result.content";

	return {
		title: ts("content"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "content",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				{row.liProfileEntryResult?.contentUrl && (
					<Link href={ensureHttpProtocol(row.liProfileEntryResult?.contentUrl)} target="_blank">
						<ExternalLink />
					</Link>
				)}
				<button
					className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						handleCopyClick(row.liProfileEntryResult?.content ?? "");
					}}
				>
					{row.liProfileEntryResult?.content}
				</button>
			</div>
		),
	};
};

export const renderLiPostLikesColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.likes";

	return {
		title: ts("likes"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "likes",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full items-center">{row.liPostResult?.likes}</div>
		),
	};
};

export const renderLiPostCommentsColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.comments";

	return {
		title: ts("comments"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "comments",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full items-center">{row.liPostResult?.comments}</div>
		),
	};
};

export const renderLiPostSharesColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_post_result.shares";

	return {
		title: ts("shares"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "shares",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full items-center">{row.liPostResult?.shares}</div>
		),
	};
};

export const renderCompanyCityColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.city";

	return {
		title: ts("companyCity"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "city",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={() =>
						row.prospect?.organization?.city && handleCopyClick(row.prospect?.organization?.city)
					}
				>
					{row.prospect?.organization?.city}
				</button>
			</div>
		),
	};
};

export const renderCompanyStateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.state";

	return {
		title: ts("companyState"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "state",
		hideable: true,
		moveable: true,
		show: false,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={() =>
						row.prospect?.organization?.state && handleCopyClick(row.prospect?.organization?.state)
					}
				>
					{row.prospect?.organization?.state}
				</button>
			</div>
		),
	};
};

export const renderCompanyCountryColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "organization.country";

	return {
		title: ts("companyCountry"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "country",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex flex-row">
				<button
					className="cursor-pointer"
					type="button"
					onClick={() =>
						row.prospect?.organization?.country &&
						handleCopyClick(row.prospect?.organization?.country)
					}
				>
					{row.prospect?.organization?.country}
				</button>
			</div>
		),
	};
};

export const renderEventColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_event_entry.name";

	return {
		title: ts("event"),
		width: "fit-content",
		uid: "event",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				<Tooltip content={<>{row.liEventEntry?.name}</>}>
					<Link href={ensureHttpProtocol(row.liEventEntry?.url)} target="_blank">
						<ExternalLink />
					</Link>
					<button
						className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.liEventEntry?.name || "");
						}}
					>
						{row.liEventEntry?.name}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderLiEventAttendeesColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_event_entry.attendees";

	return {
		title: ts("eventAttendees"),
		minWidth: "100px",
		uid: "eventAttendees",
		hideable: true,
		moveable: true,
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		show: true,
		render: (row: ListPerson) => (
			<div>
				<button
					className="cursor-pointer"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						handleCopyClick(row.liEventEntry?.attendees?.toString() || "");
					}}
				>
					{row.liEventEntry?.attendees}
				</button>
			</div>
		),
	};
};

export const renderLiEventDateColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_event_entry.eventAt";

	return {
		title: ts("eventAt"),
		width: "fit-content",
		uid: "eventAt",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div>
				<button
					className="cursor-pointer"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						handleCopyClick(row.liEventEntry?.eventAt || "");
					}}
				>
					{row.liEventEntry?.eventAt
						? format(new Date(row.liEventEntry.eventAt as string), "dd.MM.yyyy")
						: ""}
				</button>
			</div>
		),
	};
};

export const renderLiProfileContentResponseColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_profile_entry_result.contentResponse";

	return {
		title: ts("contentResponse"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contentResponse",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				{row.liProfileEntryResult?.contentResponseUrl && (
					<Link
						href={ensureHttpProtocol(row.liProfileEntryResult?.contentResponseUrl)}
						target="_blank"
					>
						<ExternalLink />
					</Link>
				)}
				<button
					className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						handleCopyClick(row.liProfileEntryResult?.contentResponse ?? "");
					}}
				>
					{row.liProfileEntryResult?.contentResponse}
				</button>
			</div>
		),
	};
};

export const renderLiCompanyActivityContentColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_company_activity_entry_result.content";

	function truncate(str: string, n: number) {
		return str.length > n ? str.slice(0, n - 1) + "..." : str;
	}

	return {
		title: ts("content"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "content",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				{row.liCompanyActivityEntryResult?.contentUrl && (
					<Link
						href={ensureHttpProtocol(row.liCompanyActivityEntryResult?.contentUrl)}
						target="_blank"
					>
						<ExternalLink />
					</Link>
				)}
				<Tooltip
					content={
						<div className="flex flex-col min-w-[300px] text-sm">
							<div>
								<span
									className="leading-6 truncate ..."
									style={{
										textWrap: "wrap",
									}}
								>
									{truncate(row.liCompanyActivityEntryResult?.content ?? "", 180)}
								</span>
							</div>
						</div>
					}
				>
					<button
						className="text-ellipsis overflow-hidden max-w-[196px] cursor-pointer"
						onClick={(e: ReactMouseEvent) => {
							e.preventDefault();
							e.stopPropagation();
							handleCopyClick(row.liCompanyActivityEntryResult?.content ?? "");
						}}
					>
						{row.liCompanyActivityEntryResult?.content}
					</button>
				</Tooltip>
			</div>
		),
	};
};

export const renderLiCompanyActivityContentResponseColumn = (
	ts: (key: string) => string,
	handleCopyClick: (value: string) => void,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_company_activity_entry_result.contentResponse";

	return {
		title: ts("contentResponse"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "contentResponse",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex w-full max-w-[220px] items-center">
				{row.liCompanyActivityEntryResult?.contentResponseUrl && (
					<Link
						href={ensureHttpProtocol(row.liCompanyActivityEntryResult?.contentResponseUrl)}
						target="_blank"
					>
						<ExternalLink />
					</Link>
				)}
				<button
					className="text-ellipsis overflow-hidden max-w-[250px] cursor-pointer"
					onClick={(e: ReactMouseEvent) => {
						e.preventDefault();
						e.stopPropagation();
						handleCopyClick(row.liCompanyActivityEntryResult?.contentResponse ?? "");
					}}
				>
					{row.liCompanyActivityEntryResult?.contentResponse}
				</button>
			</div>
		),
	};
};

export const renderLiCompanyActivityTypeColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_company_activity_entry_result.type";

	return {
		title: ts("type"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "type",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<div className="flex items-center">{row.liCompanyActivityEntryResult?.type}</div>
		),
	};
};

export const renderLiCompanyActivityConnectedAtColumn = (
	ts: (key: string) => string,
	sortingInbox: SortParams
): TableColumn => {
	const orderTitle = "li_company_activity_entry_result.contentDate";

	return {
		title: ts("followSince"),
		width: "fit-content",
		orderTitle,
		orderStatus: getOrderStatus(sortingInbox, orderTitle),
		uid: "followSince",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => {
			return (
				<div className="flex items-center">
					{row.liCompanyActivityEntryResult?.contentDate
						? format(new Date(row.liCompanyActivityEntryResult?.contentDate as string), "MMMM yyyy")
						: ""}
				</div>
			);
		},
	};
};

export const renderTrackerSignalColumn = (): TableColumn => {
	return {
		title: "Signal",
		width: "fit-content",
		minWidth: "200px",
		uid: "trackerSignal",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => <TrackerSignalCell signal={row.trackerSignalResult} />,
	};
};

export const renderTrackerPersonsColumn = (): TableColumn => {
	return {
		title: "Persons",
		width: "fit-content",
		minWidth: "150px",
		uid: "trackerPersons",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<TrackerPersonsCell
				currentProspectId={row.prospect?.id}
				listPersonId={row.id}
				trackerPersons={row.trackerSignalResult?.trackerOrganization?.trackerPersons}
			/>
		),
	};
};

// Signal Score component for displaying and editing signal priority in tracker lists
export const RenderSignalScore: FunctionComponent<{
	listPerson: ListPerson;
	trackerSignalResultId?: number | null;
	signalPriority?: string | null;
	signalReasoning?: string | null;
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
}> = ({ listPerson: _listPerson, trackerSignalResultId, signalPriority, signalReasoning }) => {
	const [localSignalPriority, setLocalSignalPriority] = useState<string | null>(
		signalPriority || null
	);

	const [isVisible, setIsVisible] = useState(false);
	const [updateSignalPriority] = useUpdateSignalPriorityMutation();

	const tooltipRef = useRef<HTMLDivElement>(null);
	const triggerRef = useRef<HTMLDivElement>(null);

	// Normalize signal priority for display ("Prio 1" -> "1")
	const normalizeSignalForState = (signal: string | null | undefined): string | null => {
		if (!signal) {
			return null;
		}

		if (signal === "Disqualified") {
			return "Disqualified";
		}

		const match = signal.match(/\d/);

		return match ? match[0] : null;
	};

	const normalizedPriority = normalizeSignalForState(localSignalPriority);

	const getSignalDisplayScore = (): string => {
		if (!normalizedPriority) {
			return "?";
		}

		if (normalizedPriority === "Disqualified") {
			return "DQ";
		}

		return `S${normalizedPriority}`;
	};

	const displayScore = getSignalDisplayScore();

	const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		e.stopPropagation();

		if (trackerSignalResultId && localSignalPriority) {
			await updateSignalPriority({ trackerSignalResultId, priority: localSignalPriority }).unwrap();
		}

		setIsVisible(false);
	};

	useCloseOnOutsideClick(tooltipRef, isVisible, setIsVisible);

	useAnchoredTooltip(triggerRef, tooltipRef, isVisible);

	const renderChangeResult = () => {
		return ReactDOM.createPortal(
			<div
				ref={tooltipRef}
				className="fixed flex flex-col z-100 p-4 gap-4 bg-white rounded-lg shadow-lg z-[199] min-w-[280px]"
				onClick={(e) => e.stopPropagation()}
				onMouseDown={(e) => e.stopPropagation()}
			>
				<div className="flex flex-col gap-2">
					<div className="text-sm font-semibold text-gray-700">Signal Priority</div>
					<div className="flex flex-row gap-2">
						<div
							className={classNames(
								"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-success border font-bold bg-background-success rounded-full justify-center items-center",
								normalizeSignalForState(localSignalPriority) === "1"
									? "border-blue-action border-2"
									: "border-text-success"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Prio 1");
							}}
						>
							S1
						</div>
						<div
							className={classNames(
								"cursor-pointer flex w-[24px] h-[24px] text-xxs text-text-warning border font-bold bg-background-warning rounded-full justify-center items-center",
								normalizeSignalForState(localSignalPriority) === "2"
									? "border-blue-action border-2"
									: "border-text-warning"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Prio 2");
							}}
						>
							S2
						</div>
						<div
							className={classNames(
								"cursor-pointer w-[24px] h-[24px] border text-error text-xxs font-bold bg-error-light rounded-full justify-center items-center flex",
								normalizeSignalForState(localSignalPriority) === "3"
									? "border-blue-action border-2"
									: "border-error"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Prio 3");
							}}
						>
							S3
						</div>
						<div
							className={classNames(
								"cursor-pointer w-[24px] h-[24px] text-white text-xxs font-bold bg-error rounded-full justify-center items-center flex",
								localSignalPriority === "Disqualified"
									? "border-blue-action border-2"
									: "border-error"
							)}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								setLocalSignalPriority("Disqualified");
							}}
						>
							DQ
						</div>
					</div>
				</div>

				<div className="flex flex-row gap-2 justify-end">
					<Button color={ButtonColor.ACTION} title="Save" onClick={handleSave} />
				</div>
			</div>,
			document.body
		);
	};

	const generateTooltipContent = () => {
		const signalDisplay = localSignalPriority || "Not set";
		const reasoningHtml = signalReasoning || "";

		return `
			<div>
				<div><strong>Signal:</strong> ${signalDisplay}</div>
				${reasoningHtml ? `<div class="mt-2">${reasoningHtml}</div>` : ""}
			</div>
		`;
	};

	return (
		<>
			{isVisible && renderChangeResult()}
			<Tooltip
				content={
					// eslint-disable-next-line @eslint-react/dom/no-dangerously-set-innerhtml
					<div dangerouslySetInnerHTML={{ __html: generateTooltipContent() }} />
				}
				delay={300}
			>
				<div
					ref={triggerRef}
					className={classNames(
						"cursor-pointer w-[24px] h-[24px] text-xxs font-bold rounded-full justify-center items-center flex",
						displayScore === "?"
							? "text-text-meta bg-border-light"
							: displayScore === "DQ"
								? "text-white bg-error"
								: displayScore === "S1"
									? "text-text-success border border-text-success bg-background-success"
									: displayScore === "S2"
										? "text-text-warning border border-text-warning bg-background-warning"
										: "text-error border border-error bg-error-light"
					)}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsVisible(!isVisible);
					}}
				>
					{displayScore}
				</div>
			</Tooltip>
		</>
	);
};

export const renderDeepResearchStatusColumn = (
	onResearchClick?: (row: ListPerson) => void
): TableColumn => {
	return {
		title: "Research",
		width: "fit-content",
		minWidth: "60px",
		uid: "deepResearchStatus",
		hideable: true,
		moveable: true,
		show: true,
		render: (row: ListPerson) => (
			<DeepResearchStatusCell row={row} onResearchClick={onResearchClick} />
		),
	};
};

// Company-fact columns live in ./columns/company-facts-columns.tsx; re-exported here because
// every TableColumnConfigs/* file imports its columns from this module.
export { renderFoundedYearColumn, renderIndustryColumn, renderNormalizedCompanyNameColumn };

export { EditableNameColumn };
